'use client';

import { useSupabaseAuth } from '@/components/auth/SupabaseAuthProvider';
import { resetContextChipsOnAuthChange } from '@/lib/redux/slices/uiSettingsSlice';
import type { AppDispatch } from '@/lib/redux/store';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

/**
 * Resets the ephemeral context-chip selection (#6376) whenever the authenticated
 * user changes.
 *
 * Sign-out is a soft `router.push('/')` (no full reload — see UserMenuContent),
 * and `uiSettings` is neither redux-persisted nor reset on auth change. Without
 * this, switching accounts within a single SPA session would leak User A's chip
 * selection + touched flag into User B's composer (cross-account leak).
 *
 * We watch the raw Supabase user id (the earliest, most reliable auth signal,
 * ahead of the backend `/users/me` round-trip) and clear both fields on every
 * transition between distinct identities — which covers sign-out (A → null) AND
 * sign-in (null → B), and therefore the A → B account switch. The very first
 * observation only establishes the baseline (no dispatch). A returning user's
 * session resolving asynchronously on load (null → uuid) DOES fire one reset,
 * but it is always a no-op: both fields are ephemeral, start empty, and nothing
 * can set chips before auth resolves.
 *
 * Renders nothing — pure side-effect, mounted alongside the other layout
 * initializers.
 */
export default function ContextChipAuthReset() {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSupabaseAuth();
  const userId = user?.id ?? null;

  // `undefined` sentinel = not yet observed (distinct from a signed-out `null`).
  const prevUserIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const prevUserId = prevUserIdRef.current;
    prevUserIdRef.current = userId;

    // First observation: establish the baseline without resetting.
    if (prevUserId === undefined) return;
    // No identity change → nothing to clear (e.g. token refresh).
    if (prevUserId === userId) return;

    dispatch(resetContextChipsOnAuthChange());
  }, [userId, dispatch]);

  return null;
}
